import React, { useState, useEffect } from 'react';
import { Dropdown, Input } from 'antd';
import CheckMarkMenu from '../../../../components/CheckMarkMenu/CheckMarkMenu';
import { getReplacedString } from '../../../../helpers/getReplacedString';
import useFilterState from '../../hooks/useFilterState';
import FilterItemContent from '../FilterItemContent/FilterItemContent';
import { getValueByPath } from '../../../../helpers/getValueByPath';

const completeStatuses = [
  'Выполнена',
  'Выполнена(контроль качества)',
  'Закрыта',
];

const FilterDropdown = ({
  renderButton,
  items,
  filter,
  valueKey = 'id',
  getItemContent,
  disabled = false,
  getSearchContent,
  menuClassName,
  showCheckbox = false,
  multiple = true,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterState, setFilterState] = useFilterState(filter);

  const handleMenuClick = (item) => {
    const value = getValueByPath(item, valueKey);

    if (multiple) {
      const newItems = selectedItems.includes(value)
        ? selectedItems.filter((selectedItem) => selectedItem !== value)
        : [...selectedItems, value];

      setSelectedItems(newItems);
    } else {
      if (selectedItems[0] !== value) {
        setFilterState([value]);
      }

      setDropdownVisible(false);
    }
  };

  const handleMultySelect = (parentItem) => {
    if (!parentItem.childrens || !Array.isArray(parentItem.childrens)) {
      return;
    }

    const childrenValues = parentItem.childrens.map((child) =>
      getValueByPath(child, valueKey)
    );

    const newSelectedItems = [...selectedItems];
    childrenValues.forEach((value) => {
      if (!newSelectedItems.includes(value)) {
        newSelectedItems.push(value);
      }
    });

    setSelectedItems(newSelectedItems);
  };

  const handleMultyDeselect = (parentItem) => {
    if (!parentItem.childrens || !Array.isArray(parentItem.childrens)) {
      return;
    }

    const childrenValues = parentItem.childrens.map((child) =>
      getValueByPath(child, valueKey)
    );

    const newSelectedItems = selectedItems.filter(
      (item) => !childrenValues.includes(item)
    );

    setSelectedItems(newSelectedItems);
  };

  const isParentItemChecked = (parentItem) => {
    if (!parentItem.childrens || !Array.isArray(parentItem.childrens)) {
      return false;
    }

    const childrenValues = parentItem.childrens.map((child) =>
      getValueByPath(child, valueKey)
    );

    return childrenValues.every((value) => selectedItems.includes(value));
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const handleSelectAll = () => {
    const values = filteredItems.flatMap((item) => {
      if (item.childrens && Array.isArray(item.childrens)) {
        return item.childrens.map((child) => getValueByPath(child, valueKey));
      }
      return getValueByPath(item, valueKey);
    });

    setSelectedItems(values);
  };

  const handleConfirm = () => {
    setFilterState(selectedItems);
    setDropdownVisible(false);
  };

  const handleSearch = (e) => {
    const val = e.target.value;

    setSearchValue(val);
  };

  const handleVisibleChange = (visible) => {
    setDropdownVisible(visible);
  };

  useEffect(() => {
    let filteredItems = items || [];

    if (searchValue.trim() !== '') {
      const value = getReplacedString(searchValue);

      filteredItems = items.filter((item) => {
        const content = getReplacedString(getSearchContent(item));
        return content.includes(value);
      });
    }

    if (filter.name === 'statuses') {
      const childrens = filteredItems.filter((item) =>
        completeStatuses.includes(item?.description)
      );

      const totalAmount = childrens.reduce(
        (sum, item) => sum + (item.amount || 0),
        0
      );

      if (childrens.length > 0) {
        const mergedItem = {
          id: Math.floor(Math.random() * 100000),
          name: 'Done',
          description: 'Выполнена',
          amount: totalAmount,
          childrens,
        };

        filteredItems = [
          ...filteredItems.filter(
            (item) => !completeStatuses.includes(item.description)
          ),
          mergedItem,
        ];
      }
    }

    setFilteredItems(filteredItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, items, filter]);

  useEffect(() => {
    if (!dropdownVisible) {
      setSearchValue('');
      setSelectedItems(filterState);
    }
  }, [dropdownVisible, filterState]);

  useEffect(() => {
    setSelectedItems(filterState);
  }, [filterState]);

  return (
    <Dropdown
      disabled={disabled}
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={handleVisibleChange}
      overlay={
        <CheckMarkMenu
          showCheckbox={showCheckbox}
          parentItemHandlers={{
            onMultySelect: handleMultySelect,
            onMultyDeselect: handleMultyDeselect,
            checked: isParentItemChecked,
          }}
          items={filteredItems}
          rootClassName={menuClassName}
          visible={dropdownVisible}
          renderSearchInput={
            typeof getSearchContent === 'function'
              ? ({ inputRef }) => (
                  <Input
                    ref={inputRef}
                    onChange={handleSearch}
                    value={searchValue}
                  />
                )
              : undefined
          }
          handlers={{
            onMenuClick: handleMenuClick,
            onConfirm: multiple ? handleConfirm : undefined,
            onDeselectAll: multiple ? handleDeselectAll : undefined,
            onSelectAll: multiple ? handleSelectAll : undefined,
          }}
          selectedItems={selectedItems}
          valueKey={valueKey}
          renderContent={(item) => (
            <FilterItemContent
              postText={item.postText || null}
              text={getItemContent(item)}
              amount={item.amount || item.value}
            />
          )}
        />
      }
    >
      {renderButton({ visible: dropdownVisible })}
    </Dropdown>
  );
};

export default FilterDropdown;
